import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getPaymentStatus } from "@/lib/api";
import { trackSubscriptionStarted } from "@/lib/heycatch";
import { useEntitlements } from "@/lib/entitlements";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const { refresh } = useEntitlements();
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      polls.current += 1;
      try {
        const s = await getPaymentStatus(sessionId);
        if (cancelled) return;
        setStatus(s);
        if (s.payment_status === "paid") {
          trackSubscriptionStarted(s);
          await refresh();
          return;
        }
      } catch (e) {
        console.error(e);
      }
      if (polls.current < 12) {
        setTimeout(tick, 2000);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refresh]);

  const paid = status.payment_status === "paid";

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center animate-fade-up">
        <div
          className={`h-14 w-14 mx-auto mb-8 border flex items-center justify-center ${
            paid ? "border-terracotta text-terracotta" : "border-obsidian-600 text-bone-400"
          }`}
        >
          {paid ? <Check className="h-6 w-6" /> : <Loader2 className="h-6 w-6 animate-spin" />}
        </div>
        <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-4" data-testid="payment-status">
          {paid ? "Threshold crossed" : "Holding space…"}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl text-bone-100 mb-6 leading-tight">
          {paid ? (
            <>
              The door <em className="text-bone-300 italic">is open.</em>
            </>
          ) : (
            "Just a breath while we listen for Stripe…"
          )}
        </h1>
        <p className="font-sans text-bone-300 mb-10 leading-relaxed">
          {paid
            ? "Your entitlements are updated. Return to the workbook and continue where you left off."
            : "This usually takes a few seconds. If it lingers, refresh the page."}
        </p>
        {paid && (
          <Link to="/dashboard">
            <Button
              data-testid="return-to-workbook-btn"
              className="rounded-none px-8 py-6 bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wide text-xs transition-colors"
            >
              Return to the workbook <ArrowRight className="ml-3 h-4 w-4" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}
